/**
 * Client-side mirror of the backend subscription access rules.
 *
 * These rules MUST stay in step with
 *   baotree-backend/utils/subscriptionAccess.js
 * because the two are evaluated against the same data for the same decision.
 * When they drift the UI lies: it renders a working app while every mutation is
 * rejected by the `is-subscription-active` policy, which surfaces to the user as
 * unexplained "Your subscription is inactive" errors.
 *
 * This module is intentionally dependency-free and pure so it can be unit
 * tested and reused anywhere (hooks, guards, route gates).
 */

/** Subscription statuses that represent a currently-usable subscription. */
export const LIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing']

/** The loose shape we need off a subscription, whatever its generated type. */
export type SubscriptionLike = {
  status?: string | null
  trial_end?: unknown
  cancel_at?: unknown
  current_period_end?: unknown
  cancel_at_period_end?: boolean | null
  isManual?: boolean | null
}

export type CompanyAccessLike = {
  paymentMethod?: string | null
  subscription?: SubscriptionLike | null
}

/**
 * Parse a stored datetime into epoch milliseconds, or `null` when absent or
 * malformed. Returning `null` rather than `NaN` prevents the classic bug where
 * every `NaN` comparison is `false` and a malformed date silently grants access.
 */
export const toTime = (value: unknown): number | null => {
  if (!value) return null
  const time = new Date(value as string).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * Whether an explicitly-scheduled cancellation date has already passed.
 *
 * Local (PIA) trials have no Stripe subscription, so nothing ever emits
 * `customer.subscription.deleted` when they lapse — the date must be enforced
 * directly. Self-correcting: resuming clears the cancellation and access
 * returns immediately.
 */
export const hasScheduledCancellationPassed = (
  subscription?: SubscriptionLike | null,
  now: number = Date.now()
): boolean => {
  if (!subscription) return false

  const cancelAt = toTime(subscription.cancel_at)
  if (cancelAt !== null && cancelAt <= now) return true

  if (subscription.cancel_at_period_end === true) {
    const periodEnd = toTime(subscription.current_period_end) ?? toTime(subscription.trial_end)
    if (periodEnd !== null && periodEnd <= now) return true
  }

  return false
}

/**
 * Whether the granted access window (trial, or an explicitly cancelled period)
 * has demonstrably elapsed.
 *
 * A normal auto-renewing `active` subscription is deliberately NOT gated on
 * `current_period_end`: it rolls forward via webhook each period, and a merely
 * delayed renewal must never lock out a paying customer.
 */
export const hasAccessWindowLapsed = (
  subscription?: SubscriptionLike | null,
  now: number = Date.now()
): boolean => {
  if (!subscription) return false

  const trialEnd = toTime(subscription.trial_end)
  const trialHasEnded = subscription.status === 'trialing' && trialEnd !== null && trialEnd <= now

  return trialHasEnded || hasScheduledCancellationPassed(subscription, now)
}

/**
 * Whether the company currently has access to paid features.
 *
 * Manual (admin-granted) plans are not billed through Stripe and never expire,
 * so they bypass the time-bounding entirely.
 */
export const hasActiveSubscription = (
  company?: CompanyAccessLike | null,
  now: number = Date.now()
): boolean => {
  const subscription = company?.subscription
  if (subscription?.isManual) return true

  const hasLiveStatus = LIVE_SUBSCRIPTION_STATUSES.includes(subscription?.status || '')

  return (
    hasLiveStatus && Boolean(company?.paymentMethod) && !hasAccessWindowLapsed(subscription, now)
  )
}
